import { Component } from '@theme/component';

class StorySlider extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.track = this.querySelector('[data-story-track]');
    if (!this.track) return;
    this.slides = Array.from(this.track.children);
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.addEventListener('click', this.handleClick, options);
    this.track.addEventListener('scroll', this.sync, { ...options, passive: true });
    this.track.addEventListener('keydown', event => {
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault();
        this.advance(event.key === 'ArrowRight' ? 1 : -1);
      }
    }, options);
    this.track.addEventListener('pointerdown', event => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      this.drag = { x: event.clientX, offset: this.track.scrollLeft, id: event.pointerId };
      this.track.setPointerCapture(event.pointerId);
      this.track.style.scrollSnapType = 'none';
    }, options);
    this.track.addEventListener('pointermove', event => {
      if (!this.drag || event.pointerId !== this.drag.id) return;
      this.track.scrollLeft = this.drag.offset - (event.clientX - this.drag.x);
    }, options);
    const endDrag = () => { this.drag = null; this.track.style.scrollSnapType = ''; };
    this.track.addEventListener('pointerup', endDrag, options);
    this.track.addEventListener('pointercancel', endDrag, options);
    this.track.addEventListener('lostpointercapture', endDrag, options);
    this.track.addEventListener('dragstart', event => event.preventDefault(), options);
    this.observer = new IntersectionObserver(entries => { this.visible = entries[0].isIntersecting; });
    this.observer.observe(this);
    this.resizeObserver = new ResizeObserver(this.sync);
    this.resizeObserver.observe(this.track);
    this.index = 0;
    this.sync();
    this.timer = setInterval(() => {
      if (!this.visible || this.drag || document.hidden || this.matches(':hover,:focus-within') || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      this.advance(1);
    }, 6500);
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.controller?.abort();
    this.observer?.disconnect();
    this.resizeObserver?.disconnect();
    clearInterval(this.timer);
  }
  handleClick = event => {
    if (!(event.target instanceof Element)) return;
    const target = event.target.closest('button');
    if (!target) return;
    if (target.hasAttribute('data-story-next')) this.advance(1);
    if (target.hasAttribute('data-story-prev')) this.advance(-1);
    if (target.hasAttribute('data-story-index')) this.go(Number(target.dataset.storyIndex));
  };
  advance(direction) {
    if (this.dataset.kind !== 'portraits') { this.go(this.index + direction); return; }
    const rail = this.track;
    const max = rail.scrollWidth - rail.clientWidth;
    let left = rail.scrollLeft + direction * rail.clientWidth * .8;
    if (direction > 0 && rail.scrollLeft >= max - 2) left = 0;
    if (direction < 0 && rail.scrollLeft <= 2) left = max;
    rail.scrollTo({ left, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
  }
  go(index) {
    if (!this.slides.length) return;
    this.index = (index + this.slides.length) % this.slides.length;
    const card = this.slides[this.index];
    const rail = this.track;
    const centered = this.dataset.kind === 'details';
    const delta = card.getBoundingClientRect().left - rail.getBoundingClientRect().left;
    rail.scrollTo({ left: rail.scrollLeft + delta - (centered ? (rail.clientWidth - card.clientWidth) / 2 : 0), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
  }
  sync = () => {
    const rail = this.track;
    if (!rail || !this.slides?.length) return;
    const centered = this.dataset.kind === 'details';
    const origin = rail.getBoundingClientRect().left + (centered ? rail.clientWidth / 2 : 0);
    let distance = Infinity;
    this.slides.forEach((card, i) => {
      const rect = card.getBoundingClientRect();
      const diff = Math.abs(rect.left + (centered ? rect.width / 2 : 0) - origin);
      if (diff < distance) { distance = diff; this.index = i; }
    });
    this.querySelectorAll('[data-story-index]').forEach(button => button.setAttribute('aria-pressed', String(Number(button.dataset.storyIndex) === this.index)));
  };
}
if (!customElements.get('ms-story-slider')) customElements.define('ms-story-slider', StorySlider);
